/**
 * PreferenceDollarScale
 *
 * Visual dollar sign scale for price sensitivity (1-4 $)
 * Animated fill on mount with tooltip for full description.
 */
import { Box, HStack, Text, VStack, Tooltip } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { DollarSign } from 'lucide-react'
import { parsePriceLevel, getPriceLevelLabel } from './preferenceUtils'
import { PreferenceCardHeader } from './PreferenceCardHeader'

const MotionBox = motion.create(Box)

interface PreferenceDollarScaleProps {
  /** The full price sensitivity description text */
  value: string
  /** Maximum number of dollar signs (default 4) */
  maxLevel?: number
}

export function PreferenceDollarScale({ value, maxLevel = 4 }: PreferenceDollarScaleProps) {
  const level = parsePriceLevel(value)
  const label = getPriceLevelLabel(level)

  // For "indifferent", show a special state
  const isIndifferent = level === 4

  return (
    <Tooltip
      label={value}
      placement="top"
      hasArrow
      bg="gray.800"
      color="white"
      fontSize="xs"
      px={3}
      py={2}
      borderRadius="var(--r-chip)"
      maxW="280px"
    >
      <Box
        bg="var(--card-surface-2)"
        borderRadius="var(--r-card-compact)"
        p={3}
        h="100%"
        cursor="pointer"
        _hover={{ bg: 'var(--card-chip-hover)' }}
        transition="background 0.2s"
      >
        <VStack align="stretch" spacing={2}>
          {/* Row 1: Header */}
          <PreferenceCardHeader icon={DollarSign} label="Price Range" color="green.400" />

          {/* Row 2: Value */}
          <Text fontSize="sm" fontWeight="medium" color="var(--card-text)" textAlign="center">
            {label}
          </Text>

          {/* Row 3: Dollar scale */}
          <HStack spacing={1} justify="center">
            {Array.from({ length: maxLevel }).map((_, index) => {
              // For indifferent, all are partially active
              // For others, active if index < level
              const isActive = isIndifferent ? true : index < level
              const opacity = isIndifferent ? 0.5 : 1

              return (
                <MotionBox
                  key={index}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                  }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.3,
                    ease: 'easeOut',
                  }}
                >
                  <Box
                    p={1.5}
                    borderRadius="var(--r-chip)"
                    bg={isActive ? 'green.500' : 'var(--card-chip-hover)'}
                    color={isActive ? 'white' : 'var(--card-chip-border)'}
                    opacity={opacity}
                    transition="all 0.3s"
                  >
                    <DollarSign size={18} />
                  </Box>
                </MotionBox>
              )
            })}
          </HStack>

          {/* Special indicator for indifferent */}
          {isIndifferent && (
            <Text fontSize="xs" color="var(--card-text-2)" textAlign="center">
              No price preference
            </Text>
          )}
        </VStack>
      </Box>
    </Tooltip>
  )
}
